import { keepPreviousData, useMutation, useMutationState, useQueries, useQuery, useQueryClient, type QueryClient } from '@tanstack/react-query'
import { api } from '../../api/client'
import type { components } from '../../api/generated/schema'
import { keys } from '../../api/keys'
import { parseMoney, signedTransactionCents } from '../../api/parseMoney'
import { emit } from '../../platform/analytics/analytics'
import type { AsyncState } from '../../platform/async/Async'
import { useToast } from '../../platform/feedback/ToastProvider'
import { fromQuery } from '../../platform/async/fromQuery'
import { MAX_RULES } from './builderState'
import { fromResponse, type ReadRule, type toWrite } from './ruleWire'

export type Batch = components['schemas']['BatchResult']
export type SaveResult = { rule: ReadRule; backfill: Batch | null }
export type RuleWriteBody = ReturnType<typeof toWrite> & {
  priority?: number
  apply_to_past: boolean
  backfill_start_date: string | null
}
export type PreviewBody = components['schemas']['RulePreviewRequest']

/** One matched transaction, in the shape the screen needs: `amountCents` is
 *  integer cents in DISPLAY sign, never the wire's decimal string. */
export type PreviewSample = {
  transactionId: string
  merchantName: string | null
  date: string
  amountCents: number
  changes: { [key: string]: unknown }[]
}
export type PreviewResult = {
  matchedCount: number
  totalEvaluated: number
  samples: PreviewSample[]
}

/** Carries the HTTP `status` — `error` is FastAPI's bare `{detail}` body and
 *  the status lives on the sibling `response`. See `MerchantsError`. */
export class RulesError extends Error {
  readonly body: unknown
  readonly status: number

  constructor(body: unknown, status: number) {
    super(`Rules request failed with ${status}`)
    this.name = 'RulesError'
    this.body = body
    this.status = status
  }
}

const PAGE_SIZE = 100
const DEFAULT_PRIORITY = 100

/**
 * EVERY page, in priority order. Rule order is the content — the list numbers
 * rules by position — so a first-page-only read would silently misnumber
 * rule 101 onwards. Cache shape is `ReadRule[]`; optimistic reorders write
 * into exactly this.
 */
export async function listAllRules(): Promise<ReadRule[]> {
  const out: ReadRule[] = []
  for (let page = 1; ; page++) {
    const { data, error, response } = await api.GET('/api/v1/rules', {
      params: { query: { page, page_size: PAGE_SIZE } },
    })
    if (error !== undefined || data === undefined) throw new RulesError(error, response.status)
    out.push(...data.rules.map(fromResponse))
    if (!data.has_more || data.rules.length === 0) return out
  }
}

export function useRules(): AsyncState<ReadRule[]> {
  const query = useQuery({ queryKey: keys.rules.list(), queryFn: listAllRules })
  return fromQuery(query, {
    message: () => 'Rules could not be loaded.',
    recovery: { label: 'Try again', onAct: () => void query.refetch() },
    isEmpty: (rs) => rs.length === 0,
  })
}

function saved(data: components['schemas']['RuleSaveResponse']): SaveResult {
  const { backfill, ...rule } = data
  return { rule: fromResponse(rule), backfill: backfill ?? null }
}

// Every create and update refreshes rules, transactions and Cashflow, whether or not the response carries a
// backfill: whether a save backfills is the server's rule (`save_triggers_backfill`), and gating on it would
// let a change there, or a backfill that returns no batch, leave /transactions stale. Delete touches none (#439).
export function useCreateRule() {
  const client = useQueryClient()
  return useMutation<SaveResult, RulesError, RuleWriteBody>({
    mutationFn: async (body) => {
      const { data, error, response } = await api.POST('/api/v1/rules', {
        // The generated type makes `priority` required because the schema
        // gives it a default; 100 is that default, restated so an omitted
        // priority still means what the server would have said.
        // Rules this client creates stop at the first match (plan Q7). Sent
        // on create ONLY: the builder shows no control for it, so an edit that
        // re-sent it would silently change a rule saved elsewhere with false.
        body: { ...body, priority: body.priority ?? DEFAULT_PRIORITY, stop_processing: true },
      })
      if (error !== undefined || data === undefined) throw new RulesError(error, response.status)
      return saved(data)
    },
    onSuccess: () => invalidateAfterRuleWrite(client),
  })
}

export function useUpdateRule() {
  const client = useQueryClient()
  return useMutation<SaveResult, RulesError, { id: string; body: Partial<RuleWriteBody> }>({
    mutationFn: async ({ id, body }) => {
      const { data, error, response } = await api.PATCH('/api/v1/rules/{rule_id}', {
        params: { path: { rule_id: id } },
        body,
      })
      if (error !== undefined || data === undefined) throw new RulesError(error, response.status)
      return saved(data)
    },
    onSuccess: () => invalidateAfterRuleWrite(client),
  })
}

export function useDeleteRule() {
  const client = useQueryClient()
  return useMutation<void, RulesError, string>({
    mutationFn: async (id) => {
      // A 204 has no body, so `data === undefined` is the success case.
      const { error, response } = await api.DELETE('/api/v1/rules/{rule_id}', {
        params: { path: { rule_id: id } },
      })
      if (error !== undefined) throw new RulesError(error, response.status)
    },
    onSuccess: () => {
      // Here, so every delete counts, whichever dialog ran it. No name, no id.
      emit('rule_deleted')
      return client.invalidateQueries({ queryKey: keys.rules.all() })
    },
  })
}

/**
 * `ids` top first. The backend runs the HIGHEST priority first
 * (`ORDER BY priority DESC`), so the top row gets the largest number:
 * 1000 ids give 1000 … 1. The schema bounds priority to 1…1000, so past 1000
 * rules the first 1000 keep distinct priorities and the rest share 1, rather
 * than anything out of range (the request is never rejected; the tail's mutual
 * order falls to `created_at`). The UI stops
 * creating at `MAX_RULES`, so this only covers lists made elsewhere.
 */
export function prioritiesFor(ids: string[]): { id: string; priority: number }[] {
  const top = Math.min(ids.length, MAX_RULES)
  return ids.map((id, i) => ({ id, priority: Math.max(1, top - i) }))
}

/** One bulk PATCH, no optimism: for a caller that follows it with a write. */
export async function renumberRules(ids: string[]): Promise<void> {
  const { error, response } = await api.PATCH('/api/v1/rules/reorder', {
    body: { rules: prioritiesFor(ids) },
  })
  if (error !== undefined) throw new RulesError(error, response.status)
}

/** `method`: how the order was changed — the one thing worth counting about it. */
export type ReorderCall = { ids: string[]; method: 'buttons' | 'drag' }

const REORDER_KEY = [...keys.rules.all(), 'reorder'] as const

/** The cached list in `ids` order; anything not named keeps its place after. */
function reordered(rules: ReadRule[], ids: string[]): ReadRule[] {
  const byId = new Map(rules.map((r) => [r.rule.id, r]))
  const named = ids.flatMap((id) => byId.get(id) ?? [])
  const rest = rules.filter((r) => !ids.includes(r.rule.id))
  return [...named, ...rest]
}

/**
 * Optimistic: the new order is written into the list cache at once, and put
 * back if the server refuses. Living here rather than in a component means
 * every caller gets the rollback.
 */
export function useReorderRules() {
  const client = useQueryClient()
  // Here, not in a per-call `mutate` option: TanStack fires those only for the
  // latest call, so a superseded first move that fails would roll back silently.
  const toast = useToast()
  return useMutation<ReadRule[], RulesError, ReorderCall, { previous: ReadRule[] | undefined }>({
    mutationKey: REORDER_KEY,
    // Hook-level `onSuccess` below, for the reason `toast` above is: a
    // per-call option fires only for the latest of several overlapping moves.
    mutationFn: async ({ ids }) => {
      const { data, error, response } = await api.PATCH('/api/v1/rules/reorder', {
        body: { rules: prioritiesFor(ids) },
      })
      if (error !== undefined || data === undefined) throw new RulesError(error, response.status)
      return data.map(fromResponse)
    },
    onMutate: async ({ ids }) => {
      await client.cancelQueries({ queryKey: keys.rules.all() })
      const previous = client.getQueryData<ReadRule[]>(keys.rules.list())
      if (previous !== undefined) client.setQueryData(keys.rules.list(), reordered(previous, ids))
      return { previous }
    },
    onSuccess: (_rules, { method }) => emit('rule_reordered', { method }),
    onError: (_err, _call, ctx) => {
      if (ctx?.previous !== undefined) client.setQueryData(keys.rules.list(), ctx.previous)
      toast.show({
        kind: 'error',
        message: "Couldn't reorder. Your rules are back in their previous order.",
      })
    },
    onSettled: () => {
      // Only the last of several overlapping reorders refetches; an earlier
      // one would pull the server's half-applied order over the newer
      // optimistic one.
      if (client.isMutating({ mutationKey: REORDER_KEY }) > 1) return
      return client.invalidateQueries({ queryKey: keys.rules.all() })
    },
  })
}

const BACKFILL_LIMIT = 5000

/** Matches an apply leaves as they are for a reason other than a lock. */
export function alreadySetOf(c: Batch): number {
  return c.skipped_overwrites + c.already_correct + c.skipped_outranked + c.skipped_excluded
}

/**
 * Dry run of what Save's `apply_to_past` backfill would write.
 *
 * `ignore_locks: false` is load-bearing: the endpoint's default is TRUE, which
 * previews `/apply` (it overrules the user's locks) and would over-count what
 * the backfill actually does. `respect_user_edits: true` says the same thing in
 * the newer switch, which wins over `ignore_locks` server-side. That is the
 * backfill's value, because it is fill-only; Apply now and Apply to selected
 * send `respectUserEdits(overwrite)` instead (`useRuleApply.ts`), `false` when
 * the user asked to change values they set. `overwrite_existing: false`
 * because the backfill is fill-only.
 *
 * `atLeast` is the server's `truncated`: it stopped at the limit or its time
 * budget. `alreadySet` counts every match left as it is: filed another way,
 * already right, claimed by a higher rule, or removed from this rule.
 *
 * `from` null OMITS `date_from` (ruling 8): the server then counts from its own
 * `today - 90 days`, the same clock the backfill uses. Passing the client's
 * `windowStart(null, …)` instead could be a day off the server's, and the
 * count would describe a window the backfill does not write.
 */
export async function fetchBackfillPreview(id: string, from: string | null) {
  const { data, error, response } = await api.GET('/api/v1/rules/{rule_id}/apply/preview', {
    params: {
      path: { rule_id: id },
      query: {
        overwrite_existing: false,
        ignore_locks: false,
        respect_user_edits: true,
        ...(from === null ? {} : { date_from: from }),
        limit: BACKFILL_LIMIT,
        sample_limit: 1,
      },
    },
  })
  if (error !== undefined || data === undefined) throw new RulesError(error, response.status)
  const c = data.counts
  return {
    count: c.modified,
    atLeast: c.truncated,
    skippedLocked: c.skipped_locked,
    alreadySet: alreadySetOf(c),
  }
}

/** A batch revert's outcome. `reverted`/`failed` count log entries; `changed` and `locked` count
 *  changes the revert left because the field changed since, or the user set it themselves. */
export type Reverted = { reverted: number; failed: number; changed: number; locked: number }

/**
 * What a write to a rule's history makes stale: the rule (its log, matches, exclusions and the list's
 * counts, all under the `rules` root), the transactions it touched, and the Cashflow built from them.
 * The one list every apply, Remove and Undo uses (rv-architect M1: Undo's hand-written copy had
 * drifted once, without Cashflow).
 */
export function invalidateAfterRuleWrite(client: QueryClient) {
  return Promise.all([
    client.invalidateQueries({ queryKey: keys.rules.all() }),
    client.invalidateQueries({ queryKey: keys.transactions.all() }),
    client.invalidateQueries({ queryKey: keys.cashflow.all() }),
  ])
}

export function useRevertBatch() {
  const client = useQueryClient()
  return useMutation<Reverted, RulesError, string>({
    mutationFn: async (batchId) => {
      const { data, error, response } = await api.POST('/api/v1/rule-logs/batch/{batch_id}/revert', {
        params: { path: { batch_id: batchId } },
      })
      if (error !== undefined || data === undefined) throw new RulesError(error, response.status)
      return { reverted: data.reverted, failed: data.failed, changed: data.skipped_changed, locked: data.skipped_locked }
    },
    onSuccess: (result) => {
      emit('rule_batch_reverted', { transaction_count: result.reverted, error_count: result.failed })
      return invalidateAfterRuleWrite(client)
    },
  })
}

async function previewUnsaved(body: PreviewBody): Promise<PreviewResult> {
  const { data, error, response } = await api.POST('/api/v1/rules/preview', { body })
  if (error !== undefined || data === undefined) throw new RulesError(error, response.status)
  return {
    matchedCount: data.matched_count,
    totalEvaluated: data.total_evaluated,
    samples: data.samples.map((s) => ({
      transactionId: s.transaction_id,
      merchantName: s.merchant_name,
      date: s.transaction_date,
      // `amount` is a decimal STRING, expense-positive in storage; parsed once
      // here and re-signed for display, as `useMerchants` does.
      amountCents: signedTransactionCents(parseMoney(s.amount)),
      changes: s.changes,
    })),
  }
}

/** Dry run of an UNSAVED rule. `null` parks the query. */
export function useUnsavedPreview(body: PreviewBody | null): {
  data?: PreviewResult
  isFetching: boolean
  isError: boolean
  /** `data` answers an OLDER body (kept across a key change). */
  isPlaceholderData: boolean
} {
  const query = useQuery({
    queryKey: keys.rules.preview(body),
    queryFn: () => previewUnsaved(body as PreviewBody),
    enabled: body !== null,
    placeholderData: keepPreviousData,
  })
  return { data: query.data, isFetching: query.isFetching, isError: query.isError, isPlaceholderData: query.isPlaceholderData }
}

/** A server-detected pattern, in the shape the screen needs. */
export type RuleSuggestion = {
  id: string
  pattern: string
  categoryId: string
  categoryName: string
  count: number
  /** At most three, in the server's order. */
  exampleIds: string[]
}

const SAMPLE_LIMIT = 3

async function listPendingSuggestions(): Promise<RuleSuggestion[]> {
  const { data, error, response } = await api.GET('/api/v1/rule-suggestions', {
    params: { query: { status: 'pending', page_size: PAGE_SIZE } },
  })
  if (error !== undefined || data === undefined) throw new RulesError(error, response.status)
  return data.suggestions.map((s) => ({
    id: s.id,
    pattern: s.merchant_pattern,
    categoryId: s.suggested_category_id,
    categoryName: s.suggested_category_name,
    count: s.matching_transaction_count,
    // The wire types the ids as `{}[]`; anything that is not a string is not an id.
    exampleIds: s.example_transaction_ids.filter((x): x is string => typeof x === 'string').slice(0, SAMPLE_LIMIT),
  }))
}

/** Pending suggestions — a live surface: there is no sync to be behind. */
export function useRuleSuggestions(): AsyncState<RuleSuggestion[]> {
  const query = useQuery({ queryKey: keys.ruleSuggestions.list({ status: 'pending' }), queryFn: listPendingSuggestions })
  return fromQuery(query, {
    message: () => 'Suggestions could not be loaded.',
    recovery: { label: 'Try again', onAct: () => void query.refetch() },
    isEmpty: (s) => s.length === 0,
  })
}

const DISMISS_KEY = [...keys.ruleSuggestions.all(), 'dismiss'] as const

/** The ids of every dismiss in flight — not just the latest call, which is all
 *  `mutation.variables` can see. */
export function usePendingDismissals(): ReadonlySet<string> {
  const ids = useMutationState({
    filters: { mutationKey: DISMISS_KEY, status: 'pending' },
    select: (m) => (m.state.variables as { id: string } | undefined)?.id,
  })
  return new Set(ids.filter((id): id is string => id !== undefined))
}

/** The backend answers a dismiss of one already dismissed with a 400 whose
 *  detail names the status (rule_suggestions.py `dismiss_suggestion`). When a
 *  success response is lost the retry gets exactly this, and the dismiss HAS
 *  happened, so it is a success. Any other status is not. */
function alreadyDismissed(error: unknown, status: number): boolean {
  if (status !== 400 || typeof error !== 'object' || error === null) return false
  const detail = (error as { detail?: unknown }).detail
  return typeof detail === 'string' && detail.includes("already 'dismissed'")
}

/** Dismissed suggestions do not come back (M11-R13). The card leaves at once. */
export function useDismissSuggestion({ onFailed }: { onFailed?: (id: string) => void } = {}) {
  const client = useQueryClient()
  const toast = useToast()
  // `afterSave`: the dismiss follows a rule that was just saved, so the failure
  // says the rule is safe rather than implying nothing happened.
  return useMutation<void, RulesError, { id: string; afterSave?: boolean }>({
    mutationKey: DISMISS_KEY,
    mutationFn: async ({ id }) => {
      const { error, response } = await api.POST('/api/v1/rule-suggestions/{suggestion_id}/dismiss', {
        params: { path: { suggestion_id: id } },
      })
      if (error !== undefined && !alreadyDismissed(error, response.status)) throw new RulesError(error, response.status)
    },
    onSuccess: (_void, { id, afterSave }) => {
      // A dismiss that follows a saved rule is the suggestion being USED, and
      // Rules emits `rule_suggestion_used` for it; only a person's own Dismiss
      // is a dismissal.
      if (!afterSave) emit('rule_suggestion_dismissed')
      // The LIST prefix only: the samples live under the same root.
      const list = { queryKey: [...keys.ruleSuggestions.all(), 'list'] }
      client.setQueriesData<RuleSuggestion[]>(list, (old) => old?.filter((s) => s.id !== id))
      return client.invalidateQueries(list)
    },
    onError: (_err, { id, afterSave }) => {
      // Hook level, not per-`mutate`: TanStack runs a call's own callbacks only
      // for the LATEST call, but this runs for every request.
      onFailed?.(id)
      toast.show({
        kind: 'error',
        message: afterSave
          ? "Your rule is saved, but this suggestion couldn't be dismissed."
          : "Couldn't dismiss this suggestion. Try again.",
      })
    },
  })
}

/** One illustrating transaction: `amountCents` is integer cents in DISPLAY sign. */
export type SuggestionSample = {
  id: string
  name: string
  date: string
  amountCents: number
  currency: string
}

/**
 * The samples, fetched in parallel. A failed one (a transaction deleted since
 * detection is a 404) is omitted: a sample illustrates the claim, it is not
 * the claim, so it is never an error and never a placeholder.
 */
export function useSuggestionSamples(ids: readonly string[]): { samples: SuggestionSample[] } {
  const results = useQueries({
    queries: ids.map((id) => ({
      queryKey: [...keys.ruleSuggestions.all(), 'sample', id],
      retry: false,
      queryFn: async (): Promise<SuggestionSample> => {
        const { data, error, response } = await api.GET('/api/v1/transactions/{transaction_id}', {
          params: { path: { transaction_id: id } },
        })
        if (error !== undefined || data === undefined) throw new RulesError(error, response.status)
        return {
          id: data.id,
          name: data.name,
          date: data.transaction_date,
          amountCents: signedTransactionCents(parseMoney(data.amount)),
          currency: data.iso_currency_code,
        }
      },
    })),
  })
  return {
    samples: results.flatMap((r) => (r.data === undefined ? [] : [r.data])),
  }
}
